import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type FC,
  type PropsWithChildren,
} from 'react';
import { useSelector } from 'react-redux';
import { useChargebeeSiteName } from '@bbkAdminComponents/optimizer/OptimizationHelper';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { IntegrationDataType } from '@bbkAdminRedux/data-management/reducers';
import { useSelectCurrentApp } from '@bbkAdminRedux/rtkq/current-user.slice';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { loadCopilotScript } from '@bbkAdminUtils/load-copilot-script';
import { useGetProductLicense } from '@bbkAdminUtils/plans';

const COPILOT_CONTAINER_ID = 'copilot-container';

export type OpenCopilotPayload = {
  message?: string;
  startNewSession?: boolean;
};

type CopilotContextValue = {
  openCopilot: (payload?: OpenCopilotPayload) => void;
  closeCopilot: () => void;
  isAvailable: boolean;
};

const CopilotContext = createContext<CopilotContextValue | null>(null);

export function useCopilot(): CopilotContextValue | null {
  return useContext(CopilotContext);
}

const CopilotProviderInner: FC<PropsWithChildren> = ({ children }) => {
  const productLicense = useGetProductLicense();
  const growthV2License = productLicense?.growthV2License;
  const isCopilotFeatureFlag = useFeatureFlags(
    FeatureFlagsE.ENABLE_CHARGEBEE_COPILOT
  );
  const appKey = useSelector(selectAppId);
  const billingIntegration = useSelectBillingIntegration({ appKey });
  const isChargebeeIntegration =
    billingIntegration?.data_type === IntegrationDataType.CHARGEBEE;
  const isCopilotEnabled =
    isCopilotFeatureFlag && growthV2License && isChargebeeIntegration;
  const chargebeeSiteFromIntegration = useChargebeeSiteName(appKey);
  const site = chargebeeSiteFromIntegration?.trim();
  const app = useSelectCurrentApp();
  const region = app?.cb_site_region || 'us';
  const mountedRef = useRef(false);
  const mountedSiteRef = useRef<string | null>(null);
  const mountedRegionRef = useRef<string | null>(null);

  const openCopilot = useCallback((payload?: OpenCopilotPayload) => {
    if (typeof window === 'undefined') return;
    try {
      if (window.ChargebeeCopilot) {
        window.ChargebeeCopilot.open(payload ?? {});
        return;
      }
    } catch (err) {
      console.error('Failed to open Copilot', err);
      return;
    }
    loadCopilotScript()
      .then(() => window.ChargebeeCopilot?.open(payload ?? {}))
      .catch((err) => console.error('Failed to open Copilot', err));
  }, []);

  const closeCopilot = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      window.ChargebeeCopilot?.close();
    } catch (err) {
      console.error('Failed to close Copilot', err);
    }
  }, []);

  useEffect(() => {
    if (!isCopilotEnabled || !site) return;

    const container = document.getElementById(COPILOT_CONTAINER_ID);
    if (!container) return;
    if (
      mountedRef.current &&
      mountedSiteRef.current === site &&
      mountedRegionRef.current === region
    )
      return;

    let cancelled = false;

    loadCopilotScript()
      .then(() => {
        if (cancelled || !window.ChargebeeCopilot) return;
        if (
          mountedRef.current &&
          mountedSiteRef.current === site &&
          mountedRegionRef.current === region
        )
          return;
        window.ChargebeeCopilot.mount({
          container,
          site,
          region,
        });
        mountedRef.current = true;
        mountedSiteRef.current = site;
        mountedRegionRef.current = region;
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load Chargebee Copilot', err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isCopilotEnabled, site, region]);

  useEffect(() => {
    if (isCopilotEnabled) return;
    if (!mountedRef.current) return;

    try {
      window.ChargebeeCopilot?.close();
    } catch {
      // ignore
    }
    mountedRef.current = false;
    mountedSiteRef.current = null;
    mountedRegionRef.current = null;
  }, [isCopilotEnabled]);

  const value = useMemo<CopilotContextValue>(
    () => ({
      openCopilot,
      closeCopilot,
      isAvailable: Boolean(isCopilotEnabled && site),
    }),
    [openCopilot, closeCopilot, isCopilotEnabled, site]
  );

  return (
    <CopilotContext.Provider value={value}>
      {children}
      {isCopilotEnabled && (
        <div id={COPILOT_CONTAINER_ID} className="tw-z-[9999]" />
      )}
    </CopilotContext.Provider>
  );
};

/**
 * Error boundary so a Copilot failure never crashes the main app.
 * On error, renders appChildren directly with a null context (copilot unavailable)
 * — bypassing CopilotProviderInner entirely to avoid re-triggering the crash.
 */
class CopilotErrorBoundary extends React.Component<
  PropsWithChildren & { appChildren: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('CopilotProvider error — copilot disabled', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <CopilotContext.Provider value={null}>
          {this.props.appChildren}
        </CopilotContext.Provider>
      );
    }
    return this.props.children;
  }
}

export const CopilotProvider: FC<PropsWithChildren> = ({ children }) => (
  <CopilotErrorBoundary appChildren={children}>
    <CopilotProviderInner>{children}</CopilotProviderInner>
  </CopilotErrorBoundary>
);
